import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumBookingStatus,
  GenqlError,
} from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { File, PlusCircle, Trash2, Undo2, FileCheck2 } from 'lucide-react';

import {
  $markAsShipped,
  $markAsCompleted,
  $markAsOpen,
} from '../../../_state/bookingDetail.state';
import { BookingRecord } from '../../../types/booking';
import { BookingMarkAsShippedDialog } from '../BookingMarkAsShippedDialog';

import {
  RevertBookingVisibility,
  useRevertBookingStatus,
} from './useRevertBookingStatus';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type Props = {
  booking: BookingRecord | null;
  setIsLoadDialogOpen: (open: boolean, bookingId?: string) => void;
  visibility: RevertBookingVisibility;
  displayListActions?: boolean;
  onVoid?: (booking: BookingRecord) => void;
  onRefetch?: () => void;
};

type OpenDialog = 'markAsShipped' | null;

export const useBookingDetailViewMenuItems = ({
  booking,
  setIsLoadDialogOpen,
  visibility,
  displayListActions = false,
  onVoid,
  onRefetch,
}: Props) => {
  const toast = useToast();
  const t = useTranslations();

  const [openDialog, setOpenDialog] = React.useState<OpenDialog>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const shipBookingMutation = useSetAtom($markAsShipped);
  const openBookingMutation = useSetAtom($markAsOpen);
  const completeBookingMutation = useSetAtom($markAsCompleted);

  const handleCreateLoad = React.useCallback(() => {
    if (!booking?.id) {
      toast.error(t.bookingDetail.messages.noBookingIdToCreateLoad);
      return;
    }
    setIsLoadDialogOpen(true, booking.id);
  }, [booking, t, setIsLoadDialogOpen, toast]);

  const handleOpenMarkAsShippedDialog = React.useCallback(() => {
    setOpenDialog('markAsShipped');
  }, []);

  const handleCloseDialog = React.useCallback(() => {
    setOpenDialog(null);
  }, []);

  const handleMarkAsShippedConfirm = React.useCallback(
    async (_shippedDate?: CalendarDate) => {
      if (!booking?.id) {
        toast.error(t.bookingDetail.messages.noBookingIdToShip);
        return;
      }
      setIsLoading(true);
      try {
        await shipBookingMutation({
          id: booking.id,
          shippedDate: _shippedDate,
        });
        toast.success(t.bookingDetail.messages.shipped);
      } catch (error) {
        const errorCode = (error as GenqlError)?.errors?.[0]?.extensions?.code;
        if (errorCode === 'ETRADE00039') {
          toast.error(t.bookingDetail.messages.loadCannotBeShipped);
        } else {
          const message =
            error instanceof Error
              ? error.message
              : t.bookingDetail.messages.unknownError;
          toast.error(
            t.bookingDetail.messages.actionFailed({ action: 'ship', message }),
          );
        }
      } finally {
        onRefetch?.();
        setIsLoading(false);
      }
    },
    [booking?.id, shipBookingMutation, t, toast, onRefetch],
  );

  // Handler for completing booking
  const handleCompleteBooking = React.useCallback(async () => {
    if (!booking?.id) {
      toast.error(t.bookingDetail.messages.noBookingIdToComplete);
      return;
    }
    try {
      await completeBookingMutation({ id: booking.id });
      toast.success(t.bookingDetail.messages.completed);
    } catch (error) {
      toast.error(
        `Failed to complete booking: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      onRefetch?.();
    }
  }, [booking?.id, completeBookingMutation, t, toast, onRefetch]);

  // Revert logic
  const [revertStatus, revertLabel] = useRevertBookingStatus({
    status: booking?.status,
    visibility,
  });

  const canCreatePendingLoad = React.useMemo(
    () =>
      booking?.status === enumBookingStatus.Open ||
      booking?.status === enumBookingStatus.Shipped,
    [booking?.status],
  );

  const canMarkAsShipped = React.useMemo(
    () => booking?.status === enumBookingStatus.Open && displayListActions,
    [booking?.status, displayListActions],
  );

  const canCompleteBooking = React.useMemo(
    () => booking?.status === enumBookingStatus.Shipped && displayListActions,
    [booking?.status, displayListActions],
  );

  const handleRevert = React.useCallback(async () => {
    if (!booking?.id || !revertStatus) return;
    try {
      if (revertStatus === 'Shipped') {
        await shipBookingMutation({ id: booking.id });
        toast.success(t.bookingDetail.messages.revertedToShipped);
      } else if (revertStatus === 'Open') {
        await openBookingMutation({ id: booking.id });
        toast.success(t.bookingDetail.messages.revertedToOpen);
      }
    } catch (error) {
      const errorCode = (error as GenqlError)?.errors?.[0]?.extensions?.code;
      if (errorCode === 'ETRADE00039') {
        toast.error(t.bookingDetail.messages.loadCannotBeShipped);
      } else {
        const message =
          error instanceof Error
            ? error.message
            : t.bookingDetail.messages.unknownError;
        toast.error(
          t.bookingDetail.messages.actionFailed({ action: 'revert', message }),
        );
      }
    } finally {
      onRefetch?.();
    }
  }, [
    booking,
    revertStatus,
    shipBookingMutation,
    openBookingMutation,
    t,
    toast,
    onRefetch,
  ]);

  const handleViewLoads = React.useCallback(() => {
    window.open(`/trade/bookings/${booking?.id}?tab=loads`);
  }, [booking?.id]);

  const handleViewDocuments = React.useCallback(() => {
    window.open(`/trade/bookings/${booking?.id}?tab=documents`);
  }, [booking?.id]);

  const handleVoidBooking = React.useCallback(() => {
    if (!booking?.id) {
      toast.error(t.bookingDetail.messages.noBookingIdToVoid);
      return;
    }
    onVoid?.(booking);
  }, [booking?.id, t, toast, onVoid]);

  const renderTrigger = React.useCallback(
    (trigger: React.ReactNode) => (
      <BookingMarkAsShippedDialog
        isDialogOpen={openDialog === 'markAsShipped'}
        isLoading={isLoading}
        onClose={handleCloseDialog}
        onConfirm={handleMarkAsShippedConfirm}
      >
        {trigger}
      </BookingMarkAsShippedDialog>
    ),
    [
      booking?.id,
      openDialog,
      isLoading,
      handleCloseDialog,
      handleMarkAsShippedConfirm,
    ],
  );

  const items = React.useMemo(
    () => [
      ...(canCreatePendingLoad || (!canCreatePendingLoad && !displayListActions)
        ? [
            {
              key: 'createLoad',
              isDisabled: !canCreatePendingLoad,
              onAction: handleCreateLoad,
              label: (
                <div className="flex items-center gap-2">
                  <PlusCircle className="size-4" />
                  {t.bookingDetail.actions.createPendingLoad}
                </div>
              ),
            },
          ]
        : []),
      ...(displayListActions && booking?.status !== enumBookingStatus.Voided
        ? [
            {
              key: 'viewLoads',
              onAction: handleViewLoads,
              label: (
                <div className="flex items-center gap-2">
                  <File className="size-4" />
                  {t.bookingDetail.actions.viewLoads}
                </div>
              ),
            },
            {
              key: 'viewDocuments',
              onAction: handleViewDocuments,
              label: (
                <div className="flex items-center gap-2">
                  <File className="size-4" />
                  {t.bookingDetail.actions.viewDocuments}
                </div>
              ),
            },
          ]
        : []),
      ...(canMarkAsShipped
        ? [
            {
              key: 'markAsShipped',
              onAction: handleOpenMarkAsShippedDialog,
              label: (
                <div className="flex items-center gap-2">
                  <FileCheck2 className="size-4" />
                  {t.bookingDetail.actions.markAsShipped}
                </div>
              ),
            },
          ]
        : []),
      ...(canCompleteBooking
        ? [
            {
              key: 'completeBooking',
              onAction: handleCompleteBooking,
              label: (
                <div className="flex items-center gap-2">
                  <FileCheck2 className="size-4" />
                  {t.bookingDetail.actions.completeBooking}
                </div>
              ),
            },
          ]
        : []),
      ...(revertStatus && revertLabel
        ? [
            {
              key: 'revert',
              onAction: handleRevert,
              label: (
                <div className="flex items-center gap-2">
                  <Undo2 className="size-4" />
                  {revertLabel}
                </div>
              ),
            },
          ]
        : []),
      ...(booking?.status !== enumBookingStatus.Completed &&
      booking?.status !== enumBookingStatus.Voided &&
      onVoid
        ? [
            {
              key: 'void',
              onAction: handleVoidBooking,
              label: (
                <div className="flex items-center gap-2 text-failure">
                  <Trash2 className="size-4" />
                  {t.bookingDetail.actions.voidBooking}
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      canCreatePendingLoad,
      displayListActions,
      handleCreateLoad,
      t,
      booking?.status,
      handleViewLoads,
      handleViewDocuments,
      canMarkAsShipped,
      handleOpenMarkAsShippedDialog,
      canCompleteBooking,
      handleCompleteBooking,
      revertStatus,
      revertLabel,
      handleRevert,
      onVoid,
      handleVoidBooking,
    ],
  );

  return { items, renderTrigger, isDialogOpen: openDialog !== null };
};
